// src/services/interceptors.js
import axios from 'axios';

// Create axios instance with default config
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  // Prevent axios from automatically following redirects
  // so we can handle 303 responses manually
  maxRedirects: 0,
  validateStatus: function (status) {
    // Accept status codes in the range 200-399 (including redirects)
    // This prevents axios from throwing an error for 303 responses
    return status >= 200 && status < 400;
  }
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage or your auth state management
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log the request for debugging (remove in production)
    //console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Log successful responses for debugging (remove in production)
    /*console.log(`Response received - Status: ${response.status}`, {
      url: response.config.url,
      status: response.status,
      data: response.data
    });
    */

    // Handle different status codes
    switch (response.status) {
      case 200:
        // Standard success response
        return response;

      case 201:
        // Resource created successfully
        return response;

      case 303:
        // See Other - redirect response
        console.log('303 redirect response received');
        // Don't throw error, let the service handle the redirect
        return response;

      default:
        // Other 2xx responses
        return response;
    }
  },
  (error) => {
    // Enhanced error handling
    console.error('API Error occurred:', error);

    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const errorData = error.response.data;

      console.error(`API Error - Status: ${status}`, errorData);

      switch (status) {
        case 400:
          // Bad Request
          console.error('Bad Request:', errorData);
          break;

        case 401:
          // Handle unauthorized
          console.error('Unauthorized access');
          localStorage.removeItem('authToken');
          // Only redirect to signup if we're not already on an auth page
          if (!window.location.pathname.includes('/dashboard')) {
            window.location.href = '/dashboard';
          }
          break;

        case 403:
          // Handle forbidden
          console.error('Access forbidden:', errorData);
          break;

        case 404:
          // Handle not found
          console.error('Resource not found:', errorData);
          break;

        case 422:
          // Validation errors
          console.error('Validation errors:', errorData);
          break;

        case 429:
          // Too many requests
          console.error('Rate limit exceeded:', errorData);
          break;

        case 500:
          // Server error
          console.error('Internal server error:', errorData);
          break;

        case 502:
          // Bad gateway
          console.error('Bad gateway:', errorData);
          break;

        case 503:
          // Service unavailable
          console.error('Service unavailable:', errorData);
          break;

        default:
          console.error('Unknown API Error:', errorData);
      }

      // Add additional context to the error
      error.apiStatus = status;
      error.apiData = errorData;

    } else if (error.request) {
      // Request made but no response received
      console.error('Network Error - No response received:', error.request);

      // Check if it's a timeout
      if (error.code === 'ECONNABORTED') {
        console.error('Request timeout');
        error.isTimeout = true;
      } else {
        console.error('Network connectivity issue');
        error.isNetworkError = true;
      }

    } else {
      // Error in request configuration
      console.error('Request Configuration Error:', error.message);
      error.isConfigError = true;
    }

    return Promise.reject(error);
  }
);

// Export a helper function to create requests with custom redirect handling
export const apiWithCustomRedirects = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  // Completely disable redirect following for specific endpoints
  maxRedirects: 0,
  validateStatus: function (status) {
    // Accept all HTTP status codes to handle them manually
    return status >= 200 && status < 600;
  }
});

// Apply the same interceptors to the custom instance
apiWithCustomRedirects.interceptors.request.use(api.interceptors.request.handlers[0].fulfilled);
apiWithCustomRedirects.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);


export default { api, apiWithCustomRedirects };